// features/procurement/CreateReturnPage.tsx
/**
 * Create Purchase Return — Redesigned with high-tier SaaS UI/UX.
 * Features:
 * - Direct GRN auto-population (loads items received from supplier with costs & UOMs)
 * - Standalone vendor return capability
 * - Standardized return reason tags (damaged, expired, wrong item, defect)
 * - Real-time stock credit valuation
 * - Explicit warnings regarding subsequent inventory deductions
 * - Zero silent failures with pre-validation and descriptive notifications
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Spinner } from '@/components/ui/Spinner';
import { getBranches } from '@/features/settings/services/settingsApi';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Plus, Save, Trash2, RotateCcw, Store,
    Search, CheckCircle2, AlertCircle, Info,
    ChevronRight, ChevronLeft, Package, Sparkles,
    AlertTriangle, X, ShieldAlert
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import {
    getVendors,
    getGRNs,
    createReturn,
    getProcurementErrorMessage,
    Vendor,
    GoodsReceivedNote,
} from './services/procurementApi';
import { QuickBarcodeScannerInput } from '@/components/ui/QuickBarcodeScannerInput';

// ============================================================
// TYPES
// ============================================================

interface ReturnItem {
    id: string; // local temporary key
    productId: string;
    productName: string;
    sku?: string;
    barcode?: string;
    quantity: string;
    uomId: string;
    uomName?: string;
    availableUoms: Array<{ id: string; name: string; isBaseUom?: boolean }>;
    unitCost: string;
    reason: string;
}

const RETURN_REASONS = [
    'Damaged / Broken goods',
    'Substandard quality',
    'Near expiry / Expired goods',
    'Incorrect product delivered',
    'Excess shipment / Over-delivery',
    'Supplier recall',
    'Customer return due to vendor defect',
    'Other specification mismatch',
];

export function CreateReturnPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const urlGrnId = searchParams.get('grnId');
    const user = useAuthStore((s) => s.user);
    const selectedBranchId = useAuthStore((s) => s.selectedBranchId);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [currentStep, setCurrentStep] = useState<1 | 2>(1);
    const [form, setForm] = useState({
        branchId: selectedBranchId || '',
        vendorId: '',
        grnId: urlGrnId || '',
        reason: '',
    });

    const [items, setItems] = useState<ReturnItem[]>([
        {
            id: 'item-1',
            productId: '',
            productName: '',
            quantity: '1',
            uomId: '',
            availableUoms: [],
            unitCost: '',
            reason: 'Damaged / Broken goods',
        },
    ]);

    const [activeProductDropdownIndex, setActiveProductDropdownIndex] = useState<number | null>(null);
    const [productSearch, setProductSearch] = useState('');
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    // ============================================================
    // QUERIES
    // ============================================================

    const { data: branchesData, isLoading: isLoadingBranches } = useQuery({
        queryKey: ['branches-return', user?.tenantId],
        queryFn: () => getBranches(user!.tenantId),
        enabled: !!user?.tenantId,
    });

    const { data: vendorsData, isLoading: isLoadingVendors } = useQuery<Vendor[]>({
        queryKey: ['vendors-return', user?.tenantId],
        queryFn: () => getVendors(user!.tenantId, { limit: 100 }),
        enabled: !!user?.tenantId,
    });

    const { data: grnsData, isLoading: isLoadingGRNs } = useQuery<GoodsReceivedNote[]>({
        queryKey: ['grns-return', user?.tenantId],
        queryFn: () => getGRNs(user!.tenantId, { limit: 100 }),
        enabled: !!user?.tenantId,
    });

    const { data: productsData } = useQuery({
        queryKey: ['products-return', user?.tenantId, productSearch],
        queryFn: async () => {
            const params: any = { limit: 50 };
            if (productSearch.trim()) params.search = productSearch.trim();
            const { data } = await apiClient.get(`/tenants/${user!.tenantId}/products`, { params });
            if (Array.isArray(data)) return data;
            if (data?.data && Array.isArray(data.data)) return data.data;
            return [];
        },
        enabled: !!user?.tenantId,
    });

    const branches = branchesData || [];
    const vendors = vendorsData || [];
    const grns = grnsData || [];
    const products = productsData || [];
    const selectedGRN = grns.find(g => g.id === form.grnId);

    // ============================================================
    // GRN PRE-FILL EFFECT
    // ============================================================

    const handleGRNSelect = (grnId: string) => {
        const grn = grns.find(g => g.id === grnId);
        if (grn) {
            setForm(prev => ({
                ...prev,
                grnId,
                vendorId: grn.vendorId,
                branchId: grn.branchId || prev.branchId,
                reason: prev.reason || `Goods Return against GRN #${grn.grnNumber}`,
            }));

            if (grn.items && grn.items.length > 0) {
                const mappedItems: ReturnItem[] = grn.items.map((gi: any, idx: number) => {
                    const rejected = parseFloat(gi.quantityRejected || '0');
                    const defaultReturnQty = rejected > 0 ? String(rejected) : '1';

                    return {
                        id: `grn-item-${gi.id || idx}`,
                        productId: gi.productId,
                        productName: gi.productName || 'Inventory Item',
                        sku: gi.sku,
                        quantity: defaultReturnQty,
                        uomId: gi.uomId,
                        uomName: gi.uomName || 'Unit',
                        availableUoms: [{ id: gi.uomId, name: gi.uomName || 'Unit', isBaseUom: true }],
                        unitCost: String(gi.unitCost || '0.00'),
                        reason: gi.rejectionReason || 'Damaged / Broken goods',
                    };
                });
                setItems(mappedItems);
            }
        } else {
            setForm(prev => ({ ...prev, grnId }));
        }
    };

    useEffect(() => {
        if (urlGrnId && grns.length > 0 && form.grnId !== urlGrnId) {
            handleGRNSelect(urlGrnId);
        }
    }, [urlGrnId, grns]);

    // ============================================================
    // MUTATION
    // ============================================================

    const createReturnMutation = useMutation({
        mutationFn: async () => {
            const errors: string[] = [];

            if (!form.branchId) errors.push('Please select a Branch location where stock is held.');
            if (!form.vendorId) errors.push('Please select the Supplier / Vendor to return to.');

            const validItems = items.filter(i => i.productId && parseFloat(i.quantity || '0') > 0);
            if (validItems.length === 0) {
                errors.push('At least one item with valid product, return quantity, and cost is required.');
            }

            items.forEach((item, idx) => {
                if (!item.productId) {
                    errors.push(`Item #${idx + 1}: No product selected.`);
                } else {
                    const qty = parseFloat(item.quantity || '0');
                    if (qty <= 0) {
                        errors.push(`Item #${idx + 1} (${item.productName || 'Product'}): Return quantity must be greater than 0.`);
                    }
                    if (!item.uomId) {
                        errors.push(`Item #${idx + 1} (${item.productName || 'Product'}): Unit of Measure (UOM) is required.`);
                    }
                }
            });

            if (errors.length > 0) {
                setValidationErrors(errors);
                throw new Error(errors[0]);
            }

            setValidationErrors([]);

            return createReturn(user!.tenantId, {
                branchId: form.branchId,
                vendorId: form.vendorId,
                grnId: form.grnId || undefined,
                reason: form.reason?.trim() || undefined,
                items: validItems.map(i => ({
                    productId: i.productId,
                    quantity: i.quantity,
                    uomId: i.uomId,
                    unitCost: i.unitCost || '0.00',
                    reason: i.reason?.trim() || undefined,
                })),
            });
        },
        onSuccess: (ret) => {
            addToast({
                type: 'success',
                title: 'Purchase Return Initiated',
                message: `Return #${ret.returnNumber} has been recorded. Review and approve to deduct inventory.`,
            });
            queryClient.invalidateQueries({ queryKey: ['returns-list'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-returns'] });
            navigate(`/procurement/returns/${ret.id}`);
        },
        onError: (err: any) => {
            const message = getProcurementErrorMessage(err, err?.message || 'Failed to create purchase return.');
            addToast({
                type: 'error',
                title: 'Return Creation Error',
                message,
            });
        },
    });

    // ============================================================
    // HANDLERS
    // ============================================================

    const addItem = () => {
        setItems(prev => [
            ...prev,
            {
                id: `return-item-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
                productId: '',
                productName: '',
                quantity: '1',
                uomId: '',
                availableUoms: [],
                unitCost: '',
                reason: 'Damaged / Broken goods',
            },
        ]);
    };

    const removeItem = (idx: number) => {
        if (items.length <= 1) {
            addToast({
                type: 'warning',
                title: 'Action Restricted',
                message: 'At least one return item row must remain.',
            });
            return;
        }
        setItems(prev => prev.filter((_, i) => i !== idx));
    };

    const updateItem = (idx: number, field: keyof ReturnItem, value: any) => {
        setItems(prev => {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], [field]: value };
            return updated;
        });
    };

    const selectProduct = (idx: number, product: any) => {
        const uoms = Array.isArray(product.uoms) && product.uoms.length > 0
            ? product.uoms
            : [{ id: product.uomId || '00000000-0000-0000-0000-000000000000', name: product.uomName || 'Unit', isBaseUom: true }];

        const defaultUom = uoms.find((u: any) => u.isBaseUom) || uoms[0];

        setItems(prev => {
            const updated = [...prev];
            updated[idx] = {
                ...updated[idx],
                productId: product.id,
                productName: product.name,
                sku: product.sku || '',
                barcode: product.barcode || '',
                availableUoms: uoms,
                uomId: defaultUom?.id || '',
                uomName: defaultUom?.name || 'Unit',
                unitCost: product.costPrice ? String(product.costPrice) : updated[idx].unitCost || '0.00',
            };
            return updated;
        });
        setActiveProductDropdownIndex(null);
    };

    const handleBarcodeScan = (scannedCode: string) => {
        const code = scannedCode.trim().toLowerCase();
        const existingIdx = items.findIndex(
            (it) => it.sku?.toLowerCase() === code || it.barcode?.toLowerCase() === code
        );

        if (existingIdx !== -1) {
            const currentQty = parseFloat(items[existingIdx].quantity || '0');
            updateItem(existingIdx, 'quantity', String(currentQty + 1));
            addToast({
                type: 'success',
                title: 'Quantity Incremented',
                message: `Scanned "${items[existingIdx].productName}" (+1 return unit)`,
            });
            return;
        }

        const match = products.find(
            (p: any) =>
                p.barcode?.toLowerCase() === code ||
                p.sku?.toLowerCase() === code
        );

        if (match) {
            const firstEmptyIdx = items.findIndex((it) => !it.productId);
            if (firstEmptyIdx !== -1) {
                selectProduct(firstEmptyIdx, match);
            } else {
                const uoms = Array.isArray(match.uoms) && match.uoms.length > 0
                    ? match.uoms
                    : [{ id: match.uomId || '00000000-0000-0000-0000-000000000000', name: match.uomName || 'Unit', isBaseUom: true }];
                const defaultUom = uoms.find((u: any) => u.isBaseUom) || uoms[0];

                setItems((prev) => [
                    ...prev,
                    {
                        id: `return-item-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
                        productId: match.id,
                        productName: match.name,
                        sku: match.sku || '',
                        barcode: match.barcode || '',
                        quantity: '1',
                        uomId: defaultUom?.id || '',
                        uomName: defaultUom?.name || 'Unit',
                        availableUoms: uoms,
                        unitCost: match.costPrice ? String(match.costPrice) : '0.00',
                        reason: 'Damaged / Broken goods',
                    },
                ]);
            }
            addToast({
                type: 'success',
                title: 'Item Added to Return',
                message: `Added "${match.name}" for return`,
            });
        } else {
            addToast({
                type: 'warning',
                title: 'Barcode Not Found',
                message: `No catalog product matches code "${scannedCode}"`,
            });
        }
    };

    // ============================================================
    // METRICS
    // ============================================================

    const totalReturnQty = items.reduce((sum, item) => sum + parseFloat(item.quantity || '0'), 0);
    const totalReturnValue = items.reduce((sum, item) => {
        const qty = parseFloat(item.quantity || '0');
        const cost = parseFloat(item.unitCost || '0');
        return sum + (isNaN(qty) || isNaN(cost) ? 0 : qty * cost);
    }, 0);

    const formatNaira = (val: number): string =>
        `₦${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const canProceedToItems = Boolean(form.branchId && form.vendorId);
    const validItemsCount = items.filter(
        i => i.productId && parseFloat(i.quantity || '0') > 0 && i.uomId
    ).length;
    const isReadyToSubmit = canProceedToItems && validItemsCount > 0;

    return (
        <div className="min-h-screen bg-slate-50/60 text-slate-900 pb-20">
            {/* Top Action Bar */}
            <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/procurement/returns')}
                                className="p-2.5 rounded-xl bg-white hover:bg-slate-100 text-slate-600 hover:text-slate-900 border border-slate-200 transition-all shadow-sm"
                                type="button"
                                title="Back to Purchase Returns"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-lg shadow-rose-500/20 text-white">
                                <RotateCcw className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900">
                                        Create Purchase Return
                                    </h1>
                                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200 uppercase tracking-wider">
                                        Debit / Credit Note
                                    </span>
                                </div>
                                <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                                    <span>{selectedGRN ? `Linked to GRN #${selectedGRN.grnNumber}` : 'General Vendor Return'}</span>
                                    <span>•</span>
                                    <span>{totalReturnQty.toLocaleString()} units marked for return</span>
                                </p>
                            </div>
                        </div>

                        {/* Top CTAs */}
                        <div className="flex items-center gap-3 self-end sm:self-auto">
                            <button
                                onClick={() => navigate('/procurement/returns')}
                                className="px-4 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold text-sm transition-all shadow-sm"
                                type="button"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => createReturnMutation.mutate()}
                                disabled={!isReadyToSubmit || createReturnMutation.isPending}
                                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-xl hover:from-rose-600 hover:to-pink-700 transition-all font-bold text-sm shadow-xl shadow-rose-500/25 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                                type="button"
                            >
                                {createReturnMutation.isPending ? (
                                    <>
                                        <Spinner size="xs" variant="white" />
                                        <span>Filing Return...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        <span>Issue Purchase Return</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-8">
                {/* Step Indicators */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                        type="button"
                        onClick={() => setCurrentStep(1)}
                        className={clsx(
                            'p-4 rounded-2xl border text-left transition-all flex items-center justify-between',
                            currentStep === 1
                                ? 'bg-white border-rose-400 text-rose-700 shadow-sm ring-2 ring-rose-500/20'
                                : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                        )}
                    >
                        <div className="flex items-center gap-3">
                            <div className={clsx(
                                'w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm',
                                canProceedToItems ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600'
                            )}>
                                {canProceedToItems ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : '1'}
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-900">Step 1: Origin & Supplier Details</p>
                                <p className="text-xs text-slate-500">Source branch, vendor, and optional GRN document link</p>
                            </div>
                        </div>
                        <ChevronRight className="w-5 h-5 opacity-40" />
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            if (canProceedToItems) setCurrentStep(2);
                            else {
                                addToast({
                                    type: 'warning',
                                    title: 'Required Details',
                                    message: 'Please set the branch location and vendor first.',
                                });
                            }
                        }}
                        className={clsx(
                            'p-4 rounded-2xl border text-left transition-all flex items-center justify-between',
                            currentStep === 2
                                ? 'bg-white border-rose-400 text-rose-700 shadow-sm ring-2 ring-rose-500/20'
                                : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                        )}
                    >
                        <div className="flex items-center gap-3">
                            <div className={clsx(
                                'w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm',
                                validItemsCount > 0 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-600'
                            )}>
                                {validItemsCount > 0 ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : '2'}
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-900">Step 2: Return Items & Fault Codes</p>
                                <p className="text-xs text-slate-500">{validItemsCount} items · Credit claim: {formatNaira(totalReturnValue)}</p>
                            </div>
                        </div>
                        <ChevronRight className="w-5 h-5 opacity-40" />
                    </button>
                </div>

                {/* Warning Alert regarding Stock Deduction */}
                <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                    <div>
                        <span className="font-bold text-amber-900">Inventory Ledger Notice:</span> Once approved, this return will immediately deduct physical stock from the selected branch warehouse and generate an accounts payable debit balance.
                    </div>
                </div>

                {/* Validation Banner if errors exist */}
                {validationErrors.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-sm flex items-start gap-3 shadow-sm"
                    >
                        <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                        <div className="space-y-1">
                            <p className="font-bold text-rose-900">Unable to submit return due to incomplete fields:</p>
                            <ul className="list-disc list-inside text-xs space-y-0.5 text-rose-700">
                                {validationErrors.map((err, i) => (
                                    <li key={i}>{err}</li>
                                ))}
                            </ul>
                        </div>
                        <button
                            onClick={() => setValidationErrors([])}
                            className="ml-auto text-rose-600 hover:text-rose-800 p-1"
                            type="button"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </motion.div>
                )}

                {/* STEP 1: ORIGIN & SUPPLIER */}
                {currentStep === 1 && (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        className="space-y-6"
                    >
                        <div className="bg-white border border-slate-200 rounded-3xl p-6 lg:p-8 shadow-sm space-y-6">
                            <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                                <Store className="w-5 h-5 text-rose-500" />
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900">Origin Facility & Supplier</h3>
                                    <p className="text-xs text-slate-500">Select which branch inventory will be deducted and the target vendor</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Optional Linked GRN */}
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                                        Source Goods Received Note (Optional)
                                    </label>
                                    <select
                                        value={form.grnId}
                                        onChange={(e) => handleGRNSelect(e.target.value)}
                                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all shadow-sm"
                                    >
                                        <option value="">Direct Return (No GRN)</option>
                                        {grns.map((g) => (
                                             <option key={g.id} value={g.id}>
                                                {g.grnNumber} — {g.vendorName} [{g.status}]
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Branch */}
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                                        Origin Branch <span className="text-rose-500">*</span>
                                    </label>
                                    <select
                                        value={form.branchId}
                                        onChange={(e) => setForm({ ...form, branchId: e.target.value })}
                                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all shadow-sm"
                                        required
                                    >
                                        <option value="">Select branch...</option>
                                        {branches.map((b: any) => (
                                            <option key={b.id} value={b.id}>
                                                {b.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Vendor */}
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                                        Supplier / Vendor <span className="text-rose-500">*</span>
                                    </label>
                                    <select
                                        value={form.vendorId}
                                        onChange={(e) => setForm({ ...form, vendorId: e.target.value })}
                                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all shadow-sm"
                                        required
                                    >
                                        <option value="">Select vendor...</option>
                                        {vendors.map((v: Vendor) => (
                                            <option key={v.id} value={v.id}>
                                                {v.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Return Reason Notes */}
                            <div>
                                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                                    General Return Reason / Narrative
                                </label>
                                <textarea
                                    value={form.reason}
                                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                                    rows={3}
                                    placeholder="Explain why items are being returned (e.g. Broken packaging on batch arrival, water damage, recall)..."
                                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-800 resize-none focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all placeholder:text-slate-400 shadow-sm"
                                />
                            </div>

                            {/* Step 1 Footer */}
                            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                                <span className="text-xs text-slate-500">
                                    {canProceedToItems ? (
                                        <span className="text-emerald-600 flex items-center gap-1 font-medium">
                                            <CheckCircle2 className="w-4 h-4" /> Ready to select damaged or rejected inventory items.
                                        </span>
                                    ) : (
                                        'Please select origin branch and vendor to proceed.'
                                    )}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(2)}
                                    disabled={!canProceedToItems}
                                    className="flex items-center gap-2 px-6 py-3 bg-rose-600 hover:bg-rose-500 text-white rounded-xl font-bold text-sm shadow-md shadow-rose-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                >
                                    <span>Select Return Items</span>
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* STEP 2: RETURN ITEMS */}
                {currentStep === 2 && (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        className="space-y-6"
                    >
                        <div className="bg-white border border-slate-200 rounded-3xl p-6 lg:p-8 shadow-sm space-y-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-slate-100">
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                        <Package className="w-5 h-5 text-rose-500" />
                                        <span>Returned Inventory Items</span>
                                    </h3>
                                    <p className="text-xs text-slate-500">
                                        Specify products to return to vendor along with unit cost for credit claim
                                    </p>
                                </div>
                                <button
                                    onClick={addItem}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 rounded-xl font-bold text-xs transition-all shadow-sm self-start sm:self-auto cursor-pointer"
                                    type="button"
                                >
                                    <Plus className="w-4 h-4" />
                                    <span>Add Item Row</span>
                                </button>
                            </div>

                            {/* Return Item Cards */}
                            {/* Quick Barcode Scanner Bar */}
                            <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-between gap-4">
                                <div className="w-full sm:w-96">
                                    <QuickBarcodeScannerInput
                                        size="sm"
                                        placeholder="Scan barcode to locate or add return item..."
                                        onScan={handleBarcodeScan}
                                    />
                                </div>
                                <span className="hidden sm:inline text-[11px] text-slate-500">
                                    USB/Bluetooth Barcode scanner ready
                                </span>
                            </div>

                            <div className="space-y-4">
                                {items.map((item, idx) => {
                                    const qty = parseFloat(item.quantity || '0');
                                    const cost = parseFloat(item.unitCost || '0');
                                    const lineTotal = (isNaN(qty) || isNaN(cost)) ? 0 : qty * cost;

                                    return (
                                        <div
                                            key={item.id}
                                            className="p-5 rounded-2xl bg-slate-50/70 border border-slate-200 hover:border-slate-300 transition-all space-y-4"
                                        >
                                            <div className="flex items-center justify-between text-xs text-slate-500">
                                                <span className="font-bold uppercase tracking-wider text-rose-600">
                                                    Return Item #{idx + 1}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(idx)}
                                                    className="text-slate-400 hover:text-rose-600 transition-colors p-1"
                                                    title="Remove row"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                                                {/* Product Catalog */}
                                                <div className="md:col-span-4 relative">
                                                    <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                                                        Product Catalog *
                                                    </label>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setActiveProductDropdownIndex(activeProductDropdownIndex === idx ? null : idx);
                                                            setProductSearch('');
                                                        }}
                                                        className="w-full px-3.5 py-2.5 bg-white border border-slate-200 hover:border-rose-400 rounded-xl text-left text-sm text-slate-800 flex items-center justify-between transition-all shadow-sm"
                                                    >
                                                        {item.productId ? (
                                                            <div className="truncate">
                                                                <p className="font-bold text-slate-900 text-xs truncate">{item.productName}</p>
                                                                {item.sku && <p className="text-[10px] text-slate-500 font-mono">SKU: {item.sku}</p>}
                                                            </div>
                                                        ) : (
                                                            <span className="text-slate-400 text-xs flex items-center gap-2">
                                                                <Search className="w-3.5 h-3.5" /> Select catalog product...
                                                            </span>
                                                        )}
                                                        <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                                                    </button>

                                                    <AnimatePresence>
                                                        {activeProductDropdownIndex === idx && (
                                                            <motion.div
                                                                initial={{ opacity: 0, y: -4 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                exit={{ opacity: 0, y: -4 }}
                                                                className="absolute top-full mt-2 w-full md:w-96 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 overflow-hidden left-0"
                                                            >
                                                                <div className="p-3 border-b border-slate-100 bg-slate-50/80">
                                                                    <div className="relative">
                                                                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                                                        <input
                                                                            type="text"
                                                                            placeholder="Search product name or SKU..."
                                                                            value={productSearch}
                                                                            onChange={(e) => setProductSearch(e.target.value)}
                                                                            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:border-rose-500"
                                                                            autoFocus
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 p-1">
                                                                    {products.length === 0 ? (
                                                                        <div className="p-4 text-center text-xs text-slate-400">
                                                                            No matching products found.
                                                                        </div>
                                                                    ) : (
                                                                        products.map((p: any) => (
                                                                            <button
                                                                                key={p.id}
                                                                                type="button"
                                                                                onClick={() => selectProduct(idx, p)}
                                                                                className={clsx(
                                                                                    'w-full text-left p-3 rounded-xl transition-all hover:bg-slate-100 flex items-center justify-between',
                                                                                    item.productId === p.id && 'bg-rose-50 border border-rose-200'
                                                                                )}
                                                                            >
                                                                                <div className="truncate pr-2">
                                                                                    <p className="text-xs font-bold text-slate-900 truncate">{p.name}</p>
                                                                                    <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500">
                                                                                        {p.sku && <span className="font-mono bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{p.sku}</span>}
                                                                                        <span>Cost: ₦{parseFloat(p.costPrice || '0').toLocaleString()}</span>
                                                                                    </div>
                                                                                </div>
                                                                                <span className="text-[10px] font-bold text-rose-700 bg-rose-50 px-2 py-1 rounded-lg shrink-0 border border-rose-200">
                                                                                    Select
                                                                                </span>
                                                                            </button>
                                                                        ))
                                                                    )}
                                                                </div>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>

                                                {/* Return Quantity */}
                                                <div className="md:col-span-2">
                                                    <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                                                        Return Qty *
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="0.001"
                                                        step="any"
                                                        value={item.quantity}
                                                        onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                                                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-right text-slate-800 focus:outline-none focus:border-rose-500 transition-all shadow-sm"
                                                        required
                                                    />
                                                </div>

                                                {/* UOM */}
                                                <div className="md:col-span-2">
                                                    <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                                                        UOM *
                                                    </label>
                                                    <select
                                                        value={item.uomId}
                                                        onChange={(e) => {
                                                            const chosen = item.availableUoms.find(u => u.id === e.target.value);
                                                            updateItem(idx, 'uomId', e.target.value);
                                                            if (chosen) updateItem(idx, 'uomName', chosen.name);
                                                        }}
                                                        disabled={!item.productId || item.availableUoms.length === 0}
                                                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:border-rose-500 disabled:opacity-40 transition-all shadow-sm"
                                                        required
                                                    >
                                                        <option value="">Select unit...</option>
                                                        {item.availableUoms.map(u => (
                                                            <option key={u.id} value={u.id}>
                                                                {u.name} {u.isBaseUom ? '(Base)' : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Unit Cost */}
                                                <div className="md:col-span-2">
                                                    <label className="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                                                        Unit Cost (₦) *
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={item.unitCost}
                                                        onChange={(e) => updateItem(idx, 'unitCost', e.target.value)}
                                                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-right text-rose-700 focus:outline-none focus:border-rose-500 transition-all shadow-sm"
                                                        required
                                                    />
                                                </div>

                                                {/* Total Line Credit */}
                                                <div className="md:col-span-2 text-right pt-2 md:pt-0">
                                                    <span className="block text-[10px] font-semibold text-slate-500 uppercase">Credit Value</span>
                                                    <p className="text-sm font-bold text-slate-900 mt-1">
                                                        {formatNaira(lineTotal)}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Reason Dropdown Subrow */}
                                            <div className="pt-2 border-t border-slate-200/80">
                                                <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
                                                    Fault / Return Reason
                                                </label>
                                                <select
                                                    value={item.reason}
                                                    onChange={(e) => updateItem(idx, 'reason', e.target.value)}
                                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:border-rose-500 shadow-sm"
                                                >
                                                    {RETURN_REASONS.map(r => (
                                                        <option key={r} value={r}>{r}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Summary Bar */}
                            <div className="pt-6 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-6">
                                <div className="text-xs text-slate-500 space-y-1 w-full md:w-auto">
                                    <p className="flex items-center gap-2 text-slate-700">
                                        <Sparkles className="w-4 h-4 text-rose-500" />
                                        <span>Supplier Credit & Debit Reconciliation</span>
                                    </p>
                                    <p className="text-[11px] text-slate-500">
                                        Generates debit memo claim against vendor account payable balance.
                                    </p>
                                </div>

                                <div className="w-full md:w-80 bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-3">
                                    <div className="flex items-center justify-between text-xs text-slate-500">
                                        <span>Total Units Returned</span>
                                        <span className="font-semibold text-slate-900">{totalReturnQty.toLocaleString()} units</span>
                                    </div>
                                    <div className="pt-3 border-t border-slate-200 flex items-center justify-between">
                                        <span className="text-sm font-bold text-slate-900">Total Credit Claim</span>
                                        <span className="text-base font-black text-rose-600">{formatNaira(totalReturnValue)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep(1)}
                                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold text-sm transition-all shadow-sm"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    <span>Back to Origin</span>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => createReturnMutation.mutate()}
                                    disabled={!isReadyToSubmit || createReturnMutation.isPending}
                                    className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-xl hover:from-rose-600 hover:to-pink-700 transition-all font-bold text-sm shadow-xl shadow-rose-500/25 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                                >
                                    {createReturnMutation.isPending ? (
                                        <>
                                            <Spinner size="xs" variant="white" />
                                            <span>Filing Purchase Return...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4" />
                                            <span>Issue Purchase Return ({formatNaira(totalReturnValue)})</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
}